import { useEffect, useState } from "react";
import { Download, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const W = 1200;
const H = 630;

function drawCard(canvas, { percentage, score, total, name, dateLabel }) {
  const ctx = canvas.getContext("2d");
  canvas.width = W;
  canvas.height = H;

  ctx.fillStyle = "#0B1B4D";
  ctx.fillRect(0, 0, W, H);

  ctx.save();
  ctx.globalAlpha = 0.07;
  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.arc(1120, 60, 300, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(80, 640, 220, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  ctx.fillStyle = "#1E3FAE";
  ctx.fillRect(0, 0, 14, H / 3);
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, H / 3, 14, H / 3);
  ctx.fillStyle = "#E1000F";
  ctx.fillRect(0, (H / 3) * 2, 14, H / 3);

  const display = '"Cabinet Grotesk", ui-sans-serif, system-ui, sans-serif';
  const sans = '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif';

  ctx.fillStyle = "#1E3FAE";
  ctx.fillRect(64, 58, 12, 26);
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(76, 58, 12, 26);
  ctx.fillStyle = "#E1000F";
  ctx.fillRect(88, 58, 12, 26);
  ctx.textBaseline = "alphabetic";
  ctx.font = `800 30px ${display}`;
  ctx.fillStyle = "#FFFFFF";
  ctx.fillText("INTEGRA", 116, 82);
  const w = ctx.measureText("INTEGRA ").width;
  ctx.fillStyle = "#7EA2FF";
  ctx.fillText("FRANCE", 116 + w, 82);

  ctx.font = `700 22px ${sans}`;
  ctx.fillStyle = "#7EA2FF";
  ctx.fillText("EXAMEN CIVIQUE BLANC — QUESTIONS OFFICIELLES 2026", 64, 200);

  ctx.font = `800 118px ${display}`;
  ctx.fillStyle = "#FFFFFF";
  ctx.fillText("Réussi !", 60, 320);

  ctx.beginPath();
  ctx.arc(96, 396, 26, 0, Math.PI * 2);
  ctx.fillStyle = "#10B981";
  ctx.fill();
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = 6;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(84, 397);
  ctx.lineTo(93, 406);
  ctx.lineTo(110, 386);
  ctx.stroke();
  ctx.font = `600 26px ${sans}`;
  ctx.fillStyle = "#C7D4F5";
  ctx.fillText(`${score}/${total} bonnes réponses`, 140, 405);

  ctx.font = `500 22px ${sans}`;
  ctx.fillStyle = "#8DA0CF";
  ctx.fillText(`${name ? `${name} — ` : ""}${dateLabel}`, 64, 496);

  ctx.font = `600 22px ${sans}`;
  ctx.fillStyle = "#C7D4F5";
  ctx.fillText("Préparez le vôtre sur INTEGRA FRANCE", 64, 556);
  ctx.font = `500 20px ${sans}`;
  ctx.fillStyle = "#8DA0CF";
  ctx.fillText("Votre parcours d'intégration, accompagné par l'IA", 64, 588);

  const cx = 962;
  const cy = 380;
  const r = 128;
  ctx.lineWidth = 22;
  ctx.strokeStyle = "rgba(255,255,255,0.14)";
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = "#10B981";
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + (Math.PI * 2 * percentage) / 100);
  ctx.stroke();
  ctx.textAlign = "center";
  ctx.font = `800 84px ${display}`;
  ctx.fillStyle = "#FFFFFF";
  ctx.fillText(`${percentage}%`, cx, cy + 18);
  ctx.font = `700 18px ${sans}`;
  ctx.fillStyle = "#8DA0CF";
  ctx.fillText("BONNES RÉPONSES", cx, cy + 56);
  ctx.textAlign = "left";
}

export function ShareSuccessButton({ percentage, score, total, finishedAt, small = false }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [imgUrl, setImgUrl] = useState(null);

  const dateLabel = new Date(finishedAt || Date.now()).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  const shareText = `J'ai réussi mon examen civique blanc avec ${percentage} % de bonnes réponses sur INTEGRA FRANCE ! Préparez le vôtre avec les questions officielles 2026 : ${window.location.origin}`;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (document.fonts?.ready || Promise.resolve()).then(() => {
      if (cancelled) return;
      const canvas = document.createElement("canvas");
      drawCard(canvas, { percentage, score, total, name: user?.name?.split(" ")[0], dateLabel });
      setImgUrl(canvas.toDataURL("image/png"));
    });
    return () => { cancelled = true; };
  }, [open, percentage, score, total, user, dateLabel]);

  const download = () => {
    const a = document.createElement("a");
    a.href = imgUrl;
    a.download = `integra-france-examen-reussi-${percentage}.png`;
    a.click();
    toast.success("Image téléchargée ! Partagez-la sur vos réseaux.");
  };

  const nativeShare = async () => {
    try {
      const blob = await (await fetch(imgUrl)).blob();
      const file = new File([blob], "integra-france-reussite.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: shareText });
      } else {
        await navigator.share({ text: shareText, url: window.location.origin });
      }
    } catch { /* partage annulé */ }
  };

  const networks = [
    { label: "X", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}` },
    { label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(shareText)}` },
    { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}&quote=${encodeURIComponent(shareText)}` },
    { label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}` },
  ];

  return (
    <>
      {small ? (
        <button onClick={() => setOpen(true)} title="Partager ma réussite"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-emerald-200 text-emeraude transition-colors hover:bg-emerald-50"
          data-testid="share-success-small-button">
          <Share2 className="h-3.5 w-3.5" />
        </button>
      ) : (
        <button onClick={() => setOpen(true)}
          className="mx-auto mt-3 flex items-center gap-2 rounded-full bg-emeraude px-6 py-2.5 text-xs font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-emerald-700"
          data-testid="share-success-button">
          <Share2 className="h-3.5 w-3.5" /> Partager ma réussite
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="share-success-dialog">
          <DialogHeader>
            <DialogTitle>Partagez votre réussite</DialogTitle>
            <DialogDescription>
              Téléchargez votre carte de réussite et publiez-la fièrement sur vos réseaux.
            </DialogDescription>
          </DialogHeader>
          {imgUrl ? (
            <img src={imgUrl} alt={`Carte de réussite — ${percentage} % à l'examen civique blanc`}
              className="w-full rounded-xl border border-slate-200 shadow-sm" data-testid="share-success-preview" />
          ) : (
            <div className="flex h-56 items-center justify-center rounded-xl bg-slate-50 text-xs text-slate-400">Génération de la carte…</div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={download} disabled={!imgUrl} className="btn-primary !px-5 !py-2.5 !text-xs disabled:opacity-50" data-testid="share-success-download">
              <Download className="h-3.5 w-3.5" /> Télécharger l'image
            </button>
            {typeof navigator !== "undefined" && !!navigator.share && (
              <button onClick={nativeShare} disabled={!imgUrl}
                className="btn-secondary !px-5 !py-2.5 !text-xs disabled:opacity-50" data-testid="share-success-native">
                <Share2 className="h-3.5 w-3.5" /> Partager…
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {networks.map((n) => (
              <a key={n.label} href={n.href} target="_blank" rel="noreferrer"
                className="rounded-full border border-slate-200 px-4 py-2 text-[11px] font-bold text-slate-600 transition-colors hover:border-bleu/40 hover:text-bleu"
                data-testid={`share-success-${n.label.toLowerCase()}`}>
                {n.label}
              </a>
            ))}
          </div>
          <p className="text-[10px] text-slate-400">
            Astuce : sur X, Facebook et LinkedIn, joignez l'image téléchargée à votre publication pour un maximum d'effet.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}
